import { api } from "services/axios/api";
import { IReferenciaOption } from "services/modules/tarefas";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";

// ============================================
// CONSTANTES
// ============================================

/** Id do status "Concluído" (espelha StatusConcluido = 4 do backend). */
export const STATUS_CONCLUIDO = 4;

// ============================================
// INTERFACES - STATUS
// ============================================

export interface ITarefaStatus {
  idTarefaStatus: number;
  nome: string;
  ordem: number;
  cor: string;
}

// ============================================
// INTERFACES - RESPONSÁVEL
// ============================================

export interface IResponsavelResumo {
  idUsuario: number;
  nome: string;
  iniciais: string;
  email: string;
}

// ============================================
// INTERFACES - CARD DE TAREFA (KANBAN)
// ============================================

export interface ISubtarefaCard {
  idSubtarefa: number;
  nome: string;
  idTarefaStatus: number;
  nomeStatus: string;
  horasReal: number;
  /** Tempo real em minutos inteiros (igual à planilha). Backend já projeta. */
  totalMinutosReal?: number;
  /** Item 6.5: tempo real em segundos inteiros — fonte de verdade para hh:mm:ss. */
  totalSegundosReal?: number;
  temTimerAtivo: boolean;
  responsaveis: IResponsavelResumo[];
}

export interface ITarefaCard {
  idTarefa: number;
  numero: number;
  titulo: string;
  idTarefaStatus: number;
  nomeStatus: string;
  idReferencia: number;
  nomeReferencia: string;
  idTipoAtividade: number | null;
  nomeTipoAtividade: string | null;
  previsaoInicio: string | null;
  previsaoTermino: string | null;
  horasReal: number;
  /** Tempo real em minutos inteiros (igual à planilha). Backend já projeta. */
  totalMinutosReal?: number;
  /** Item 6.5: tempo real em segundos inteiros — fonte de verdade para hh:mm:ss. */
  totalSegundosReal?: number;
  estimativaHoras: number | null;
  quantidadeSubtarefas: number;
  quantidadeSubtarefasConcluidas: number;
  temTimerAtivo: boolean;
  responsaveis: IResponsavelResumo[];
  subtarefas: ISubtarefaCard[];
}

// ============================================
// INTERFACES - QUADRO KANBAN
// ============================================

export interface IQuadroKanban {
  idEmpresa: number;
  colunas: ITarefaStatus[];
  tarefas: ITarefaCard[];
}

// ============================================
// INTERFACES - FILTROS
// ============================================

export interface IFiltroQuadroTarefas {
  idEmpresa: number;
  idReferencias?: number[];
  idResponsaveis?: number[];
  idStatus?: number[];
  tags?: string[];
}

export interface IResponsavelFiltro {
  idUsuario: number;
  nome: string;
}

export interface IStatusFiltro {
  idTarefaStatus: number;
  nome: string;
  cor: string;
}

export interface ITagFiltro {
  tag: string;
}

// ============================================
// INTERFACES - SUB-TAREFA
// ============================================

export interface ISubtarefaResumo {
  idSubtarefa: number;
  nome: string;

  idTarefaStatus: number;
  nomeStatus: string;
  ordem: number;
  horasReal: number;
  /** Tempo real em minutos inteiros (igual à planilha). Backend já projeta. */
  totalMinutosReal?: number;
  /** Item 6.5: tempo real em segundos inteiros — fonte de verdade para hh:mm:ss. */
  totalSegundosReal?: number;
  estimativaHoras: number | null;
  responsaveis: IResponsavelResumo[];
}

// ============================================
// INTERFACES - TAREFA DETALHADA
// ============================================

export interface ITarefaDetalhada {
  idTarefa: number;
  numero: number;
  titulo: string;
  descricao: string | null;
  idReferencia: number;
  nomeReferencia: string;
  idTipoAtividade: number | null;
  nomeTipoAtividade: string | null;
  idTarefaStatus: number;
  nomeStatus: string;
  idSolicitante: number | null; // Se selecionado da lista
  nomeSolicitante: string | null; // Nome completo (usuário ou texto livre)
  tarefaInterna: boolean;
  previsaoInicio: string | null;
  previsaoTermino: string | null;
  estimativaHoras: number | null;
  inicioReal: string | null;
  terminoReal: string | null;
  horasReal: number;
  /** Tempo real em minutos inteiros (igual à planilha). Backend já projeta. */
  totalMinutosReal?: number;
  /** Item 6.5: tempo real em segundos inteiros — fonte de verdade para hh:mm:ss. */
  totalSegundosReal?: number;
  idEmpresa: number;
  dataCriacao: string;
  dataAtualizacao: string | null;
  ativo: boolean;
  responsaveis: IResponsavelResumo[];
  subtarefas: ISubtarefaResumo[];
}

// ============================================
// INTERFACES - REQUESTS
// ============================================

export interface ICriarSubtarefaRequest {
  nome: string;
  descricao?: string;

  previsaoInicio?: string;
  previsaoTermino?: string;
  estimativaHoras?: number;
  ordem: number;
  idsResponsaveis: number[];
}

export interface ICriarTarefaRequest {
  titulo: string;
  descricao?: string;
  idReferencia: number;
  idTipoAtividade?: number;
  idEmpresa: number;
  idSolicitante?: number; // ID do usuário selecionado
  nomeSolicitante?: string; // Nome completo (usuário selecionado ou texto livre)
  tarefaInterna?: boolean;
  previsaoInicio?: string;
  previsaoTermino?: string;
  estimativaHoras?: number;
  idsResponsaveis: number[];
  subtarefas: ICriarSubtarefaRequest[];
}

export interface IMoverTarefaRequest {
  idTarefa: number;
  idTarefaStatusNovo: number;
}

export interface IIniciarTimerRequest {
  idTarefa?: number;
  idSubtarefa?: number;
}

export interface IPararTimerRequest {
  idTarefaTimer: number;
  observacao?: string;
}

// ============================================
// INTERFACES - TIMER
// ============================================

export interface ITimerAtivo {
  idTarefaTimer: number;
  idTarefa: number | null;
  idSubtarefa: number | null;
  tituloTarefa: string | null;
  nomeSubtarefa: string | null;
  inicio: string;
  segundosDecorridos: number;
}

// ============================================
// INTERFACES - NOTIFICAÇÕES
// ============================================

export interface INotificacaoResumo {
  idTarefaNotificacao: number;
  tipoNotificacao: string;
  titulo: string;
  mensagem: string | null;
  lida: boolean;
  dataCriacao: string;
  idTarefa: number | null;
  numeroTarefa: number | null;
}

export interface IContadorNotificacoes {
  naoLidas: number;
}

// ============================================
// SERVIÇOS - STATUS
// ============================================

/**
 * Obtém todos os status (colunas do Kanban)
 */
export const getStatus = async (): Promise<ITarefaStatus[]> => {
  try {
    const { data } = await api.get<ITarefaStatus[]>("/QuadroTarefa/ObterStatus");
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar os status");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

// ============================================
// SERVIÇOS - QUADRO KANBAN
// ============================================

/**
 * Obtém o quadro Kanban completo
 */
export const getQuadroKanban = async (
  filtro: IFiltroQuadroTarefas
): Promise<IQuadroKanban | null> => {
  try {
    const { data } = await api.post<IQuadroKanban>(
      "/QuadroTarefa/ObterQuadroKanban",
      filtro
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar o quadro de tarefas");
    return null;
  }
};

// ============================================
// SERVIÇOS - CRUD TAREFA
// ============================================

/**
 * Cria uma nova tarefa
 */
export const criarTarefa = async (
  request: ICriarTarefaRequest
): Promise<{ idTarefa: number } | null> => {
  try {
    const { data } = await api.post<{ idTarefa: number }>(
      "/QuadroTarefa/CriarTarefa",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível criar a tarefa");
    throw error;
  }
};

/**
 * Obtém tarefa detalhada
 */
export const getTarefaDetalhada = async (
  idTarefa: number
): Promise<ITarefaDetalhada | null> => {
  try {
    const { data } = await api.get<ITarefaDetalhada>(
      `/QuadroTarefa/ObterTarefaDetalhada/${idTarefa}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar a tarefa");
    return null;
  }
};

/**
 * Move tarefa para outro status (coluna)
 */
export const moverTarefa = async (
  request: IMoverTarefaRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/QuadroTarefa/MoverTarefa",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível mover a tarefa");
    throw error;
  }
};

// ============================================
// SERVIÇOS - TIMER
// ============================================

/**
 * Inicia timer para tarefa/sub-tarefa
 */
export const iniciarTimer = async (
  request: IIniciarTimerRequest
): Promise<{ idTarefaTimer: number } | null> => {
  try {
    const { data } = await api.post<{ idTarefaTimer: number }>(
      "/QuadroTarefa/IniciarTimer",
      request
    );
    return data;
  } catch (error: any) {
    console.error("[iniciarTimer] Erro:", error);
    console.error("[iniciarTimer] Error response:", error?.response);
    console.error("[iniciarTimer] Error data:", error?.response?.data);
    getErrorsByApi(error, "Não foi possível iniciar o timer");
    throw error;
  }
};

/**
 * Para timer ativo
 */
export const pararTimer = async (
  request: IPararTimerRequest
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/QuadroTarefa/PararTimer",
      request
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível parar o timer");
    throw error;
  }
};

/**
 * Obtém timer ativo do usuário
 */
export const getTimerAtivo = async (): Promise<ITimerAtivo | null> => {
  try {
    const { data } = await api.get<ITimerAtivo>(
      "/QuadroTarefa/ObterTimerAtivo"
    );
    return data;
  } catch (error: any) {
    // Não exibir erro se não houver timer ativo
    return null;
  }
};

// ============================================
// SERVIÇOS - NOTIFICAÇÕES
// ============================================

/**
 * Obtém notificações do usuário
 */
export const getNotificacoes = async (
  quantidade: number = 20
): Promise<INotificacaoResumo[]> => {
  try {
    const { data } = await api.get<INotificacaoResumo[]>(
      `/QuadroTarefa/ObterNotificacoes?quantidade=${quantidade}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível carregar as notificações");
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

/**
 * Obtém contador de notificações não lidas
 */
export const getContadorNotificacoes = async (): Promise<IContadorNotificacoes> => {
  try {
    const { data } = await api.get<IContadorNotificacoes>(
      "/QuadroTarefa/ObterContadorNotificacoes"
    );
    return data;
  } catch (error: any) {
    return { naoLidas: 0 };
  }
};

/**
 * Marca notificação como lida
 */
export const marcarNotificacaoLida = async (
  idTarefaNotificacao: number
): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      `/QuadroTarefa/MarcarNotificacaoLida/${idTarefaNotificacao}`
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível marcar a notificação como lida");
    throw error;
  }
};

/**
 * Marca todas notificações como lidas
 */
export const marcarTodasNotificacoesLidas = async (): Promise<boolean> => {
  try {
    const { data } = await api.put<boolean>(
      "/QuadroTarefa/MarcarTodasNotificacoesLidas"
    );
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível marcar as notificações como lidas");
    throw error;
  }
};

// ============================================
// SERVIÇOS - FILTROS (OPÇÕES)
// ============================================

/**
 * Obtém lista de responsáveis para filtro.
 *
 * N13.3: quando `idReferencia` é informado, o backend inclui usuários das
 * empresas que compartilham essa referência (EmpresaTarefaReferencia), além
 * dos da própria empresa. Sem ele, mantém o comportamento atual (só a empresa).
 */
export const getResponsaveisFiltro = async (
  idEmpresa: number,
  idReferencia?: number | null
): Promise<IResponsavelFiltro[]> => {
  try {
    const params: Record<string, any> = { idEmpresa };
    if (idReferencia != null) {
      params.idReferencia = idReferencia;
    }
    const { data } = await api.get<IResponsavelFiltro[]>(
      `/QuadroTarefa/ObterResponsaveisFiltro`,
      { params }
    );
    return data;
  } catch (error: any) {
    // Retorna lista vazia em caso de erro (endpoint pode não existir ainda)
    return [];
  }
};

/**
 * Obtém lista de responsáveis do quadro (apenas usuários com tarefas/sub-tarefas
 * atribuídas no quadro da empresa)
 */
export const getResponsaveisQuadro = async (
  idEmpresa: number
): Promise<IResponsavelFiltro[]> => {
  try {
    const { data } = await api.get<IResponsavelFiltro[]>(
      `/QuadroTarefa/ObterResponsaveisQuadro?idEmpresa=${idEmpresa}`
    );
    return data;
  } catch (error: any) {
    // Não mascarar falha de backend (500/timeout) como lista vazia:
    // logar e propagar para distinguir "sem dados" de "erro real".
    console.error(
      "[getResponsaveisQuadro] Falha ao carregar responsáveis do quadro:",
      error?.response?.status,
      error
    );
    getErrorsByApi(error, "Não foi possível carregar os responsáveis do quadro");
    throw error;
  }
};

/**
 * Obtém lista de referências do quadro (apenas referências vinculadas a
 * tarefas do quadro da empresa)
 */
export const getReferenciasQuadro = async (
  idEmpresa: number
): Promise<IReferenciaOption[]> => {
  try {
    const { data } = await api.get<IReferenciaOption[]>(
      `/QuadroTarefa/ObterReferenciasQuadro?idEmpresa=${idEmpresa}`
    );
    return data;
  } catch (error: any) {
    // Não mascarar falha de backend (500/timeout) como lista vazia:
    // logar e propagar para distinguir "sem dados" de "erro real".
    console.error(
      "[getReferenciasQuadro] Falha ao carregar referências do quadro:",
      error?.response?.status,
      error
    );
    getErrorsByApi(error, "Não foi possível carregar as referências do quadro");
    throw error;
  }
};

/**
 * Obtém lista de status para filtro
 */
export const getStatusFiltro = async (): Promise<IStatusFiltro[]> => {
  try {
    const { data } = await api.get<IStatusFiltro[]>(
      "/QuadroTarefa/ObterStatusFiltro"
    );
    return data;
  } catch (error: any) {
    // Retorna lista padrão em caso de erro
    return [
      { idTarefaStatus: 1, nome: "Novos", cor: "#E3F2FD" },
      { idTarefaStatus: 2, nome: "Andamento", cor: "#FFF9C4" },
      { idTarefaStatus: 3, nome: "Aguardando", cor: "#FCE4EC" },
      { idTarefaStatus: 4, nome: "Concluídos", cor: "#E8F5E9" },
    ];
  }
};

/**
 * Obtém lista de tags para filtro
 */
export const getTagsFiltro = async (
  idEmpresa: number
): Promise<ITagFiltro[]> => {
  try {
    const { data } = await api.get<ITagFiltro[]>(
      `/QuadroTarefa/ObterTagsFiltro?idEmpresa=${idEmpresa}`
    );
    return data;
  } catch (error: any) {
    // Retorna lista vazia em caso de erro (endpoint pode não existir ainda)
    return [];
  }
};

// ============================================
// SERVIÇOS - EXPORTAÇÃO DE DADOS
// ============================================

/**
 * Exporta os dados de tarefas da empresa no período informado para XLSX
 * e dispara o download do arquivo no navegador.
 * @param dataInicio Data inicial no formato yyyy-MM-dd
 * @param dataFim Data final no formato yyyy-MM-dd
 */
export const exportarDadosTarefas = async (
  idEmpresa: number,
  dataInicio: string,
  dataFim: string,
  tipoPeriodo: "criadas" | "trabalhadas" = "criadas"
): Promise<void> => {
  try {
    const response = await api.get("/Tarefa/ExportarDados", {
      params: { idEmpresa, dataInicio, dataFim, tipoPeriodo },
      responseType: "blob",
    });

    const nomeArquivo = `MOF_DADOS_EXTRAIDOS_TAREFAS_${dataInicio}_${dataFim}.xlsx`;

    // Criar link para download (padrão de downloadDocumento)
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", nomeArquivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível exportar os dados de tarefas");
    throw error;
  }
};

